import { useEffect, useState } from 'react';
import styles from "./Header.module.scss";
import { Link, useNavigate } from 'react-router-dom';
import * as authService from '../../auth/services/auth.service';
import { useTranslation } from 'react-i18next';
import { getCartItems } from '../../components/cart/services/cart.service';
import { CartItem } from '../../components/cart/models';
import { Language } from '../models';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { getCmsUrlAndTitleByLanguagAndCode } from '../../containers/cms-container/services';
import { ABOUT, CONTACT, HOW_TO_BUY } from '../constants/cms-codes.constants';
import { CmsTitlesAndUrls } from '../../containers/cms-container/models/cms-titles-url-by-code';
import { getLanguages } from '../services/language.service';
import { useMediaQuery } from '@mui/material';
import MobileSidePanel from './mobile-side-panel/MobileSidePanel';

const phoneIcon = require("../../assets/icons/phone.svg").default;
const envelopeIcon = require("../../assets/icons/envelope.svg").default;
const logoIcon = require("../../assets/icons/koszyklisiecki-logo.svg").default;
const cartIcon = require("../../assets/icons/cart.svg").default;
const handShakeIcon = require("../../assets/icons/KLUB.svg").default;
const userIcon = require("../../assets/icons/login.svg").default;

interface HeaderProps {
    selectedCategory?: string;
    isSearched: boolean;
    handleCategorySelect: any;
    updateIsSearched: any;
    cartItemsChanged: any;
}

export default function Header({ selectedCategory, isSearched, updateIsSearched, handleCategorySelect, cartItemsChanged }: HeaderProps) {
    const currency = useSelector((state: RootState) => state.currency);
    const { t, i18n } = useTranslation('translation');
    const navigate = useNavigate();
    const [cartItemsNumber, setCartItemsNumber] = useState<number>(0);
    const [cartWorth, setCartWorth] = useState<number>(0);
    const [titlesAndUrls, setTitlesAndUrls] = useState<CmsTitlesAndUrls>({});
    const [loading, setLoading] = useState(true);
    const [allLanguages, setAllLanguages] = useState<Language[]>([]);
    const isWideScreen = useMediaQuery('(min-width:768px)');

    const getTitleAndUrlForHeader = async (): Promise<void[]> => {
        const savedLanguageCode = localStorage.getItem('lang');
        if (savedLanguageCode) {
            try {
                
                const headerCodes = [HOW_TO_BUY, CONTACT, ABOUT];
                
                
                const results = await Promise.allSettled(
                    headerCodes.map(async (code) => {
                        const response = await getCmsUrlAndTitleByLanguagAndCode(code, savedLanguageCode);
                        if (response && response.data) {
                            return { code, data: response.data };
                        }
                        throw new Error(`Code ${code} not found`);
                    })
                );
                
                
                results.forEach((result) => {
                    if (result.status === 'fulfilled') {
                        const { code, data } = result.value;
                        setTitlesAndUrls((prevTitlesAndUrls) => ({
                            ...prevTitlesAndUrls,
                            [code]: data,
                        }));
                    }
                });
                
                setLoading(false);
            } catch (error) {
                console.error('Błąd podczas pobierania danych CMS dla headera:', error);
                setLoading(false);
            }
        }

        return Promise.resolve([]);
    };

    const handleSetLanguages = () => {
        getLanguages().then((result) => {
            if (result) setAllLanguages(result?.data as Language[]);
        });

    };

    useEffect(() => {
        getCartItems().then((result) => {
            if (Array.isArray(result)) {
                setCartItemsNumber(result.length as number);
                calculateCartWorth(result as CartItem[]);
            }
        })
    }, [cartItemsChanged]);

    useEffect(() => {
        handleSetLanguages();
    }, []);

    useEffect(() => {
        getTitleAndUrlForHeader();
    }, []);

    
    useEffect(() => {
        const handleLanguageChange = () => {
            getTitleAndUrlForHeader();
        };
        
        window.addEventListener('languageChanged', handleLanguageChange);
        
        return () => {
            window.removeEventListener('languageChanged', handleLanguageChange);
        };
    }, []);

    const calculateCartWorth = (items: CartItem[]): void => {
        let sum = 0;
        items?.forEach(it => {
            sum += it.totalPrice
        });
        setCartWorth(sum);
    }

    const [hamburgerOpen, setHamburgerOpen] = useState(false);

    const toggleHamburger = () => {
        setHamburgerOpen(!hamburgerOpen)
    }

    function logOut() {
        authService.logOut(navigate);
    }

    return (
        <div className={styles.main_container}>
            <div className={styles.upper_part}>
                <div className={styles.upper_part_content}>
                    <div className={styles.left_side}>

                    </div>
                    <div className={styles.right_side}>
                        {
                            !authService.checkLogInStatus() ?
                                <>
                                    <Link to="/register" className={styles.register_link}>{t("header.register")}</Link>
                                    <Link to="/login" className={styles.login_link}>{t("header.login")}</Link>
                                </>
                                :
                                <>
                                    <button type="button" className={styles.logout_button} onClick={() => logOut()}>{t("header.logout")}</button>
                                    <span className={styles.blured}>{t("header.register")}</span>
                                </>
                        }
                    </div>
                </div>
            </div>
            <div className={styles.bottom_part}>
                <div className={styles.logo_wrapper}>
                    <img onClick={() => { navigate("/"); navigate(0) }} src={logoIcon} alt="Logo" />
                </div>
                {isWideScreen ?
                    <div className={`${styles.links_container}`}>
                        <Link to="/">{t("header.shop")}</Link>
                        <Link to="/producers">{t("header.producers")}</Link>
                        {!loading && titlesAndUrls.HOW_TO_BUY?.url && <Link to={titlesAndUrls.HOW_TO_BUY.url}>{titlesAndUrls?.HOW_TO_BUY?.title}</Link>}
                        {!loading && titlesAndUrls.CONTACT?.url && <Link to={titlesAndUrls.CONTACT.url}>{titlesAndUrls.CONTACT.title}</Link>}
                        {!loading && titlesAndUrls.JOIN_US?.url && <Link to={titlesAndUrls.JOIN_US.url} className={styles.join_us}>
                            <img src={handShakeIcon} alt="Hand shake" />
                            <p>{titlesAndUrls?.JOIN_US?.title}</p>
                        </Link>}
                        {!loading && titlesAndUrls.ABOUT?.url && <Link to={titlesAndUrls.ABOUT.url} className={styles.about}>{titlesAndUrls?.ABOUT?.title}</Link>}
                    </div>
                    :
                    <>
                        {hamburgerOpen &&
                            <MobileSidePanel
                                titlesAndUrls={titlesAndUrls}
                                loading={loading}
                                closePanel={toggleHamburger}
                                isSearched={isSearched}
                                handleCategorySelect={handleCategorySelect}
                                selectedCategory={selectedCategory}
                                updateIsSearched={updateIsSearched}
                            />}
                    </>
                }
                <div className={styles.icons_container}>
                    {
                        authService.checkLogInStatus() &&
                        <Link className={styles.user_details} to="/client-panel">
                            <img src={userIcon} alt="user-icon" />
                        </Link>
                    }
                    <div className={styles.cart}>
                        <Link to="/cart">
                            <>
                                <div className={styles.quantity}>
                                    <p>{cartItemsNumber}</p>
                                </div>
                                <img src={cartIcon} alt="Cart" />
                            </>
                        </Link>
                    </div>
                </div>
                <div className={`${styles.hamburger_container}`} onClick={toggleHamburger}>
                    <div className={`${styles.hamburger} ${hamburgerOpen ? `${styles.menu_open}` : ''}`}>
                        <div className={`${styles.bar} ${styles.bar1}`}></div>
                        <div className={`${styles.bar} ${styles.bar2}`}></div>
                        <div className={`${styles.bar} ${styles.bar3}`}></div>
                    </div>
                </div>
            </div>
        </div>
    )
}